import { getInsuranceProvider, getMrns, getRiskScore, getSearchableNames } from './caseFields';

export const EMPTY_FILTERS = Object.freeze({ query: '', statuses: [], insurances: [], risks: [] });

/** True when the query has text or any chip group has a selection. */
export function hasActiveFilters(filters) {
    return filters.query.trim() !== ''
        || filters.statuses.length > 0
        || filters.insurances.length > 0
        || filters.risks.length > 0;
}

/**
 * Unique insurance values across the cases, deduped without regard to case
 * (insurance is free text from extraction, so "Aetna" and "aetna" are the
 * same insurer), sorted case-insensitively.
 *
 * The first spelling encountered wins: cases in order, then `selected`.
 *
 * `selected` (already-chosen insurance chip values) is always included in the
 * result, even when no case in `cases` currently carries that value — so a
 * chip a user picked never disappears out from under them while it is still
 * narrowing the list (e.g. after a scope change or a snapshot update).
 */
export function insuranceOptions(cases, selected = []) {
    const seen = new Map();
    for (const c of cases) {
        const value = getInsuranceProvider(c.structuredInfo);
        if (value && !seen.has(value.toLowerCase())) seen.set(value.toLowerCase(), value);
    }
    for (const value of selected) {
        if (value && !seen.has(value.toLowerCase())) seen.set(value.toLowerCase(), value);
    }
    return [...seen.values()].sort((x, y) => x.localeCompare(y, undefined, { sensitivity: 'base' }));
}

function searchHaystack(caseItem) {
    const si = caseItem.structuredInfo;
    const names = getSearchableNames(si);
    const mrns = getMrns(caseItem);

    // Build both First Last and Last, First orderings from named parts, skipping nulls.
    const firstLast = [names.first, names.last].filter(Boolean).join(' ');
    const lastFirst = [names.last, names.first].filter(Boolean).join(', ');

    const parts = [
        firstLast,
        lastFirst,
        names.legacy,
        ...mrns,
    ].filter(Boolean);

    return parts.join(' ').toLowerCase();
}

function matchesQuery(caseItem, query) {
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return true;
    const haystack = searchHaystack(caseItem);
    return tokens.every((t) => haystack.includes(t));
}

const inGroup = (selected, value) => selected.length === 0 || selected.includes(value);

/** Like inGroup, but insurance is free text from extraction, so compare without regard to case. */
const inInsuranceGroup = (selected, value) => (
    selected.length === 0 || (value != null && selected.some((s) => s.toLowerCase() === value.toLowerCase()))
);

/**
 * Apply the search query and chip groups to a list of cases.
 *
 * Args:
 *     cases: Case documents as returned by useCases (full documents).
 *     filters: { query, statuses, insurances, risks }; see EMPTY_FILTERS.
 *
 * Returns:
 *     Array: The cases that match every group, in the input order.
 */
export function applyCaseFilters(cases, filters) {
    return cases.filter((c) => (
        matchesQuery(c, filters.query)
        && inGroup(filters.statuses, c.admission_status || 'In progress')
        && inInsuranceGroup(filters.insurances, getInsuranceProvider(c.structuredInfo))
        && inGroup(filters.risks, getRiskScore(c))
    ));
}
